// Persisted progress for the first-run Build Wizard (/build → BuildWizard).
//
// Unlike the old linear Setup wizard, the Build Wizard is resumable AND re-runnable from the
// beginning or any step: it remembers the current step, the target SITE being built, and each
// step's status (todo / done / skipped) so the header can show what's left and the dashboard can
// offer "Resume" / "Restart". Cleared on Finish.

export const BUILD_WIZARD_KEY = 'bls.buildWizard.v1';

export type StepStatus = 'todo' | 'done' | 'skipped';

export interface BuildWizardProgress {
  /** Zero-based current step index. */
  step: number;
  /** The site being built (so a reload/resume stays on the same site). null → not chosen yet. */
  siteId: string | null;
  /** Per-step status keyed by step id. */
  status: Record<string, StepStatus>;
  /** Template slug applied on the Template step. */
  appliedSlug: string | null;
  /** Blog opt-in from the Content step, if reached. */
  blogEnabled: boolean | null;
}

const EMPTY: BuildWizardProgress = {
  step: 0,
  siteId: null,
  status: {},
  appliedSlug: null,
  blogEnabled: null,
};

export function loadBuildProgress(): BuildWizardProgress {
  try {
    const raw = localStorage.getItem(BUILD_WIZARD_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<BuildWizardProgress>;
      return {
        step: typeof p.step === 'number' && p.step >= 0 ? p.step : 0,
        siteId: typeof p.siteId === 'string' ? p.siteId : null,
        status: p.status && typeof p.status === 'object' ? (p.status as Record<string, StepStatus>) : {},
        appliedSlug: typeof p.appliedSlug === 'string' ? p.appliedSlug : null,
        blogEnabled: typeof p.blogEnabled === 'boolean' ? p.blogEnabled : null,
      };
    }
  } catch {
    /* ignore — corrupt/blocked storage falls back to a fresh start */
  }
  return { ...EMPTY };
}

export function saveBuildProgress(p: BuildWizardProgress): void {
  try {
    localStorage.setItem(BUILD_WIZARD_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function clearBuildProgress(): void {
  try {
    localStorage.removeItem(BUILD_WIZARD_KEY);
  } catch {
    /* ignore */
  }
}

/** True when the wizard was started but not finished — drives the dashboard's "Resume" CTA. */
export function isBuildInProgress(): boolean {
  const p = loadBuildProgress();
  return p.step > 0 || Object.keys(p.status).length > 0;
}
